// Centralized error handling utility
import logger from './logger';

/**
 * Standard error logger
 */
export const logError = (context, error, additionalInfo = {}) => {
  const errorInfo = {
    context,
    message: error?.message || 'Unknown error',
    stack: error?.stack,
    timestamp: new Date().toISOString(),
    ...additionalInfo
  };
  
  logger.error(`[${context}] Error:`, errorInfo);
  
  // You can add error reporting service here (e.g., Sentry)
  // if (window.Sentry) {
  //   window.Sentry.captureException(error, { extra: errorInfo });
  // }
  
  return errorInfo;
};

/**
 * User-friendly error messages
 */
export const getUserFriendlyError = (error) => {
  const message = error?.message || error?.toString() || 'An unexpected error occurred';
  
  // Common wallet errors
  if (message.includes('User rejected')) {
    return 'Connection cancelled by user';
  }
  if (message.includes('not installed')) {
    return message; // Already user-friendly
  }
  if (message.includes('not found')) {
    return message; // Already user-friendly
  }
  if (message.includes('Network')) {
    return 'Network error. Please check your connection and try again.';
  }
  if (message.includes('insufficient funds')) {
    return 'Insufficient SOL balance for transaction fees';
  }
  if (message.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }
  
  // Firebase errors
  if (message.includes('auth/')) {
    if (message.includes('email-already-in-use')) {
      return 'This email is already registered';
    }
    if (message.includes('weak-password')) {
      return 'Password should be at least 6 characters';
    }
    if (message.includes('invalid-email')) {
      return 'Please enter a valid email address';
    }
    if (message.includes('user-not-found')) {
      return 'No account found with this email';
    }
    if (message.includes('wrong-password')) {
      return 'Incorrect password';
    }
  }
  
  // Default fallback
  return 'Something went wrong. Please try again.';
};

/**
 * Async error wrapper for try-catch blocks
 */
export const handleAsync = async (fn, context) => {
  try {
    return await fn();
  } catch (error) {
    logError(context, error);
    throw error;
  }
};

/**
 * Safe execution wrapper that doesn't throw
 */
export const safeExecute = async (fn, defaultValue = null, context = 'Unknown') => {
  try {
    return await fn();
  } catch (error) {
    logError(context, error);
    return defaultValue;
  }
};

/**
 * Retry logic for flaky operations
 */
export const retryOperation = async (fn, maxRetries = 3, delay = 1000, context = 'Unknown') => {
  let lastError;
  
  for (let i = 0; i < maxRetries; i++) {
    try {
      return await fn();
    } catch (error) {
      lastError = error;
      logError(`${context} - Attempt ${i + 1}/${maxRetries}`, error);
      
      if (i < maxRetries - 1) {
        await new Promise(resolve => setTimeout(resolve, delay * Math.pow(2, i))); // Exponential backoff
      }
    }
  }
  
  throw lastError;
};

/**
 * Network error checker
 */
export const isNetworkError = (error) => {
  const message = error?.message || '';
  return message.includes('Network') || 
         message.includes('fetch') || 
         message.includes('CORS') ||
         message.includes('timeout') ||
         error?.code === 'NETWORK_ERROR';
};

/**
 * Wallet error checker
 */
export const isWalletError = (error) => {
  const message = error?.message || '';
  return message.includes('wallet') || 
         message.includes('Wallet') ||
         message.includes('publicKey') ||
         message.includes('connect') ||
         message.includes('Phantom') ||
         message.includes('Solflare');
};

export default {
  logError,
  getUserFriendlyError,
  handleAsync,
  safeExecute,
  retryOperation,
  isNetworkError,
  isWalletError
};